import React, { useState, useEffect } from 'react';
import { Edit3, Save, RefreshCw, Check, AlertCircle, Calendar } from 'lucide-react';
import axios from '@/lib/http';
import { useAuth } from '../../contexts/TestAuthContext';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { formatCurrency } from '@/utils/financialFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Top expense categories for editing (expanded for restaurants)
const TOP_EXPENSE_CATEGORIES = [
  'COGS - Variable',
  'COGS - Fixed',
  'Food and Beverage Inventory',
  'Payroll',
  'Officers Payroll',
  'Rent Expense',
  'Utilities',
  'Insurance',
  'Advertising Expense',
  'Supplies',
  'Maintenance',
  'Auto Expense',
  'Professional Services',
  'Contract Labor',
  'Fuel Expense',
  'Travel & Entertainment',
  'Depreciation',
  'Interest Expense'
];

interface EditableFinancialGridProps {
  reportId: string;
  financialData: any;
  onRecalculated?: (newResult: any) => void;
}

// Read total payroll / officer comp from a month object, tolerating BOTH the
// canonical wizard shape (payroll: <number> + officer_compensation sibling — the
// shape Step 3's Monthly P&L and the BI engine use) AND the legacy nested shape
// (payroll: { total, officer_compensation }). The grid historically read only the
// nested shape, so canonical drafts showed $0 for payroll/officer comp even when
// the data was present.
const readTotalPayroll = (md: any): number => {
  if (!md) return 0;
  if (md.payroll && typeof md.payroll === 'object') return Number(md.payroll.total) || 0;
  return Number(md.payroll) || 0;
};
const readOfficerComp = (md: any): number => {
  if (!md) return 0;
  if (md.officer_compensation != null) return Number(md.officer_compensation) || 0;
  if (md.payroll && typeof md.payroll === 'object') return Number(md.payroll.officer_compensation) || 0;
  return 0;
};

export default function EditableFinancialGrid({ 
  reportId, 
  financialData, 
  onRecalculated 
}: EditableFinancialGridProps) {
  // DYNAMIC: Extract years from financial data
  const years = React.useMemo(() => {
    if (!financialData) return [];
    
    const extractedYears = Object.keys(financialData)
      .filter(key => key.startsWith('data_'))
      .map(key => key.replace('data_', ''))
      .filter(year => !isNaN(parseInt(year)))
      .sort((a, b) => parseInt(a) - parseInt(b));
    
    console.log('✅ EditableFinancialGrid: Dynamic years extracted:', extractedYears);
    return extractedYears;
  }, [financialData]);
  
  // DEBUG: Log what financial data we received
  console.log('📊 EditableFinancialGrid received financialData:', financialData);
  years.forEach(year => {
    console.log(`📊 data_${year}:`, financialData?.[`data_${year}`]?.length || 0, 'months');
  });
  
  const [editedData, setEditedData] = useState(financialData);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [recalcResult, setRecalcResult] = useState<any>(null);
  const [editedCells, setEditedCells] = useState<Set<string>>(new Set());
  const [gridMessage, setGridMessage] = useState<string | null>(null);
  const [gridError, setGridError] = useState<string | null>(null);
  const { getToken } = useAuth();

  // Auto-save when data changes
  useEffect(() => {
    // Debounced auto-save logic can go here
  }, [editedData]);

  const handleCellEdit = (year: string, monthIndex: number, field: string, value: string) => {
    const cellKey = `${year}-${monthIndex}-${field}`;
    setEditedCells(prev => new Set(prev).add(cellKey));
    
    setEditedData((prev: any) => {
      const newData = { ...prev };
      const yearKey = `data_${year}`;
      const monthData = [...(newData[yearKey] || [])];
      
      if (!monthData[monthIndex]) {
        monthData[monthIndex] = { year, month: monthIndex + 1, revenue: 0, expenses: {}, payroll: 0, employee_wages: 0, officer_compensation: 0 };
      }

      if (field === 'revenue') {
        monthData[monthIndex].revenue = parseFloat(value) || 0;
      } else if (field === 'total_payroll') {
        // Canonical shape: `payroll` is the total (number); officer comp is a
        // subset. Keep payroll = employee_wages + officer_compensation invariant.
        const newTotal = parseFloat(value) || 0;
        const officer = readOfficerComp(monthData[monthIndex]);
        monthData[monthIndex].payroll = newTotal;
        monthData[monthIndex].officer_compensation = officer;
        monthData[monthIndex].employee_wages = Math.max(0, newTotal - officer);
      } else if (field === 'officer_compensation') {
        // Officer comp is a subset of total payroll: keep the total fixed and
        // re-derive employee_wages so the invariant holds.
        const newOfficer = parseFloat(value) || 0;
        const total = readTotalPayroll(monthData[monthIndex]);
        monthData[monthIndex].payroll = total;
        monthData[monthIndex].officer_compensation = newOfficer;
        monthData[monthIndex].employee_wages = Math.max(0, total - newOfficer);
      } else {
        // Expense category
        monthData[monthIndex].expenses = {
          ...monthData[monthIndex].expenses,
          [field]: parseFloat(value) || 0
        };
      }
      
      newData[yearKey] = monthData;
      return newData;
    });
  };

  const handleRecalculate = async () => {
    setIsRecalculating(true);
    setRecalcResult(null);
    
    try {
      const token = await getToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/reports/${reportId}/recalculate`,
        { financial_data: editedData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRecalcResult(response.data);
      setEditedCells(new Set()); // Clear edited markers
      
      if (onRecalculated) {
        // ✅ FIX: Include edited financial data in callback for chart refresh
        onRecalculated({
          ...response.data,
          financial_data: editedData
        });
      }
    } catch (error: any) {
      console.error('Recalculation failed:', error);
      setGridError(`Recalculation failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsRecalculating(false);
    }
  };

  const renderMonthRow = (year: string, monthIndex: number, monthData: any) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const cellKey = (field: string) => `${year}-${monthIndex}-${field}`;
    const isEdited = (field: string) => editedCells.has(cellKey(field));
    
    const editedInputStyle = (field: string): React.CSSProperties =>
      isEdited(field)
        ? { borderColor: 'var(--color-needs-human-tint-bd)', background: 'var(--color-needs-human-tint)', color: 'var(--color-text-primary)' }
        : { borderColor: 'var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' };

    return (
      <tr
        key={`${year}-${monthIndex}`}
        style={{ borderBottom: '1px solid var(--color-border-faint)' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg-hover)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <td
          className="px-3 py-2 cs-body sticky left-0"
          style={{ fontWeight: 500, color: 'var(--color-text-secondary)', background: 'var(--color-bg-surface)' }}
        >
          {year} {monthNames[monthIndex]}
        </td>

        {/* Revenue */}
        <td className="px-3 py-2">
          <input
            type="number"
            value={monthData?.revenue || 0}
            onChange={(e) => handleCellEdit(year, monthIndex, 'revenue', e.target.value)}
            className="w-full px-2 py-1 cs-figure border rounded"
            style={editedInputStyle('revenue')}
          />
        </td>

        {/* Total Payroll */}
        <td className="px-3 py-2">
          <input
            type="number"
            value={readTotalPayroll(monthData)}
            onChange={(e) => handleCellEdit(year, monthIndex, 'total_payroll', e.target.value)}
            className="w-full px-2 py-1 cs-figure border rounded"
            style={editedInputStyle('total_payroll')}
          />
        </td>

        {/* Officer Compensation */}
        <td className="px-3 py-2">
          <input
            type="number"
            value={readOfficerComp(monthData)}
            onChange={(e) => handleCellEdit(year, monthIndex, 'officer_compensation', e.target.value)}
            className="w-full px-2 py-1 cs-figure border rounded"
            style={editedInputStyle('officer_compensation')}
          />
        </td>
        
        {/* ALL Expense Categories from actual data */}
        {(() => {
          // Get all unique expense categories from all years
          const allCategories = new Set<string>();
          years.forEach(y => {
            const yearKey = `data_${y}`;
            const yearData = editedData[yearKey] || [];
            yearData.forEach((md: any) => {
              if (md?.expenses) {
                Object.keys(md.expenses).forEach(cat => allCategories.add(cat));
              }
            });
          });
          
          // Sort categories in P&L order
          const categoryOrder = [
            'COGS - Variable',
            'COGS - Fixed',
            'Food and Beverage Inventory',
            'Payroll',
            'Officers Payroll',
            'Rent Expense',
            'Utilities',
            'Insurance',
            'Advertising Expense',
            'Supplies',
            'Other'
          ];
          
          const sortedCategories = Array.from(allCategories).sort((a, b) => {
            const aIndex = categoryOrder.indexOf(a);
            const bIndex = categoryOrder.indexOf(b);
            if (aIndex >= 0 && bIndex >= 0) return aIndex - bIndex;
            if (aIndex >= 0) return -1;
            if (bIndex >= 0) return 1;
            return a.localeCompare(b);
          });
          
          return sortedCategories.map((expCategory) => (
            <td key={expCategory} className="px-3 py-2">
              <input
                type="number"
                value={monthData?.expenses?.[expCategory] || 0}
                onChange={(e) => handleCellEdit(year, monthIndex, expCategory, e.target.value)}
                className="w-full px-2 py-1 cs-figure border rounded"
                style={editedInputStyle(expCategory)}
              />
            </td>
          ));
        })()}
      </tr>
    );
  };

  // Generate P&L CSV from edited data
  const generatePLFromData = (data: any) => {
    // Use the same dynamic years
    const csvYears = years;
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    
    // Collect all expense categories
    const allCategories = new Set<string>();
    csvYears.forEach(year => {
      const yearKey = `data_${year}`;
      const yearData = data[yearKey] || [];
      yearData.forEach((monthData: any) => {
        if (monthData?.expenses) {
          Object.keys(monthData.expenses).forEach(cat => allCategories.add(cat));
        }
      });
    });
    
    const categories = Array.from(allCategories).sort();
    
    // Build CSV
    let csv = 'Year,Month,Revenue,Total Payroll,Officer Comp';
    categories.forEach(cat => {
      csv += `,${cat}`;
    });
    csv += '\n';
    
    // Add data rows
    csvYears.forEach(year => {
      const yearKey = `data_${year}`;
      const yearData = data[yearKey] || [];
      
      yearData.forEach((monthData: any, idx: number) => {
        csv += `${year},${months[idx]},`;
        csv += `${monthData?.revenue || 0},`;
        csv += `${readTotalPayroll(monthData)},`;
        csv += `${readOfficerComp(monthData)}`;
        
        categories.forEach(cat => {
          csv += `,${monthData?.expenses?.[cat] || 0}`;
        });
        
        csv += '\n';
      });
    });
    
    return csv;
  };

  // Helper to set annual total for an expense category (distributes evenly across 12 months)
  const setAnnualTotal = (year: string, category: string, annualTotal: number) => {
    const monthlyAmount = annualTotal / 12;
    
    setEditedData((prev: any) => {
      const newData = { ...prev };
      const yearKey = `data_${year}`;
      const monthData = [...(newData[yearKey] || [])];
      
      for (let i = 0; i < 12; i++) {
        if (!monthData[i]) {
          monthData[i] = { year, month: i + 1, revenue: 0, expenses: {}, payroll: 0, employee_wages: 0, officer_compensation: 0 };
        }
        monthData[i].expenses = {
          ...monthData[i].expenses,
          [category]: monthlyAmount
        };
        // Mark as edited
        const cellKey = `${year}-${i}-${category}`;
        setEditedCells(prev => new Set(prev).add(cellKey));
      }
      
      newData[yearKey] = monthData;
      return newData;
    });
  };

  return (
    <div
      className="rounded-lg p-6"
      style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
    >
      {/* Quick Annual Entry Tool */}
      <div
        className="mb-6 p-4 rounded-lg"
        style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-needs-human-tint-bd)' }}
      >
        <h3 className="cs-h3 mb-3" style={{ color: 'var(--color-needs-human-text)' }}>Quick Annual Entry</h3>
        <p className="cs-meta mb-3">Set annual totals for expense categories (distributes evenly across 12 months)</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block cs-label mb-1">Category</label>
            <select id="quick-category" className="w-full rounded px-3 py-2 cs-body" style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}>
              {TOP_EXPENSE_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block cs-label mb-1">Year</label>
            <select id="quick-year" className="w-full rounded px-3 py-2 cs-body" style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}>
              <option value="2019">2019</option>
              <option value="2020">2020</option>
              <option value="2021">2021</option>
              <option value="2022">2022</option>
            </select>
          </div>
          <div>
            <label className="block cs-label mb-1">Annual Total</label>
            <input
              type="number"
              id="quick-amount"
              placeholder="192600"
              className="w-full rounded px-3 py-2 cs-figure"
              style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                const category = (document.getElementById('quick-category') as HTMLSelectElement)?.value;
                const year = (document.getElementById('quick-year') as HTMLSelectElement)?.value;
                const amount = parseFloat((document.getElementById('quick-amount') as HTMLInputElement)?.value || '0');
                if (category && year && amount > 0) {
                  setAnnualTotal(year, category, amount);
                  setGridMessage(`Set ${category} for ${year}: ${formatCurrency(amount)} (${formatCurrency(amount/12)}/month)`);
                }
              }}
              className="w-full px-4 py-2 rounded cs-label"
              style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
            >
              Apply Annual Total
            </button>
          </div>
        </div>
      </div>

      {/* Quick Monthly Entry Tool */}
      <div
        className="mb-6 p-4 rounded-lg"
        style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-needs-human-tint-bd)' }}
      >
        <h3 className="cs-h3 mb-3 flex items-center gap-2" style={{ color: 'var(--color-needs-human-text)' }}><Calendar className="w-4 h-4" /> Quick Monthly Entry</h3>
        <p className="cs-meta mb-3">Set individual amounts for each month</p>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block cs-label mb-1">Category</label>
            <select id="monthly-category" className="w-full rounded px-3 py-2 cs-body" style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}>
              {TOP_EXPENSE_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block cs-label mb-1">Year</label>
            <select id="monthly-year" className="w-full rounded px-3 py-2 cs-body" style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}>
              <option value="2019">2019</option>
              <option value="2020">2020</option>
              <option value="2021">2021</option>
              <option value="2022">2022</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 mb-4">
          {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, idx) => (
            <div key={month}>
              <label className="block cs-label mb-1">{month}</label>
              <input
                type="number"
                id={`month-${idx}`}
                placeholder="0"
                className="w-full rounded px-2 py-1 cs-figure"
                style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
              />
            </div>
          ))}
        </div>

        <button
          onClick={() => {
            const category = (document.getElementById('monthly-category') as HTMLSelectElement)?.value;
            const year = (document.getElementById('monthly-year') as HTMLSelectElement)?.value;
            
            if (!category || !year) return;
            
            const monthlyAmounts: number[] = [];
            for (let i = 0; i < 12; i++) {
              const input = document.getElementById(`month-${i}`) as HTMLInputElement;
              monthlyAmounts.push(parseFloat(input?.value || '0'));
            }
            
            // Apply monthly amounts
            setEditedData((prev: any) => {
              const newData = { ...prev };
              const yearKey = `data_${year}`;
              const monthData = [...(newData[yearKey] || [])];
              
              for (let i = 0; i < 12; i++) {
                if (!monthData[i]) {
                  monthData[i] = { year, month: i + 1, revenue: 0, expenses: {}, payroll: 0, employee_wages: 0, officer_compensation: 0 };
                }
                monthData[i].expenses = {
                  ...monthData[i].expenses,
                  [category]: monthlyAmounts[i]
                };
                // Mark as edited
                const cellKey = `${year}-${i}-${category}`;
                setEditedCells(prev => new Set(prev).add(cellKey));
              }
              
              newData[yearKey] = monthData;
              return newData;
            });
            
            const total = monthlyAmounts.reduce((sum, amt) => sum + amt, 0);
            setGridMessage(`Set ${category} for ${year}: ${formatCurrency(total)} total (monthly amounts applied)`);
            
            // Clear inputs
            for (let i = 0; i < 12; i++) {
              const input = document.getElementById(`month-${i}`) as HTMLInputElement;
              if (input) input.value = '';
            }
          }}
          className="w-full px-4 py-2 rounded cs-label"
          style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
        >
          Apply Monthly Amounts
        </button>
      </div>

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="cs-h2 flex items-center gap-2">
            <Edit3 className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
            48-Month Financial Data Editor
          </h2>
          <p className="cs-meta mt-1">
            Edit any cell • Changes saved automatically • Click Recalculate to update claim
          </p>
        </div>

        {/* Export Button */}
        <button
          onClick={() => {
            // Generate P&L from edited data
            const csv = generatePLFromData(editedData);
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `PL_Statement_Edited_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
          }}
          className="px-6 py-3 rounded-lg cs-label inline-flex items-center gap-2 transition"
          style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-brand-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-brand)'; }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export P&L to CSV
        </button>
      </div>

      {/* Recalculate Button */}
      <div className="mb-6">
        <button
          onClick={handleRecalculate}
          disabled={isRecalculating || editedCells.size === 0}
          className="flex items-center gap-2 px-6 py-3 rounded-lg cs-label transition"
          style={
            editedCells.size > 0
              ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)' }
              : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-dim)', cursor: 'not-allowed' }
          }
        >
          {isRecalculating ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Recalculating...
            </>
          ) : (
            <>
              <RefreshCw className="w-5 h-5" />
              Recalculate Claim
              {editedCells.size > 0 && (
                <span
                  className="ml-2 px-2 py-0.5 cs-chip rounded-full"
                  style={{ background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
                >
                  {editedCells.size} edits
                </span>
              )}
            </>
          )}
        </button>
      </div>

      {/* Recalculation Result */}
      {recalcResult && (
        <div
          className="mb-6 p-4 rounded-lg"
          style={
            recalcResult.delta >= 0
              ? { background: 'transparent', border: '2px solid var(--color-verified)' }
              : { background: 'var(--color-needs-human-tint)', border: '2px solid var(--color-needs-human-tint-bd)' }
          }
        >
          <div className="flex items-center gap-3">
            <Check className="w-6 h-6" style={{ color: 'var(--color-verified)' }} />
            <div className="flex-1">
              <p className="cs-h3">
                Claim Recalculated: <FinancialValue value={recalcResult.original_claim} /> → <FinancialValue value={recalcResult.new_claim} />
              </p>
              <p className="cs-meta">
                Change: {recalcResult.delta >= 0 ? '+' : ''}<FinancialValue value={recalcResult.delta} />
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Editable Grid */}
      <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid var(--color-border)' }}>
        <table className="min-w-full">
          <thead className="sticky top-0" style={{ background: 'var(--color-bg-elevated)' }}>
            <tr>
              <th className="px-3 py-3 text-left cs-label sticky left-0 z-10" style={{ background: 'var(--color-bg-elevated)' }}>
                Month
              </th>
              <th className="px-3 py-3 text-left cs-label">Revenue</th>
              <th className="px-3 py-3 text-left cs-label">Total Payroll</th>
              <th className="px-3 py-3 text-left cs-label">Officer Comp</th>
              {/* Show ALL expense categories from your P&L data */}
              {(() => {
                // Get all unique expense categories from actual data
                const allCategories = new Set<string>();
                years.forEach(year => {
                  const yearKey = `data_${year}`;
                  const yearData = editedData[yearKey] || [];
                  yearData.forEach((monthData: any) => {
                    if (monthData?.expenses) {
                      Object.keys(monthData.expenses).forEach(cat => allCategories.add(cat));
                    }
                  });
                });
                
                // Show categories in P&L order if they exist, otherwise alphabetical
                const categoryOrder = [
                  'COGS - Variable',
                  'COGS - Fixed',
                  'Food and Beverage Inventory',
                  'Payroll',
                  'Officers Payroll',
                  'Rent Expense',
                  'Utilities',
                  'Insurance',
                  'Advertising Expense',
                  'Supplies',
                  'Other'
                ];
                
                const sortedCategories = Array.from(allCategories).sort((a, b) => {
                  const aIndex = categoryOrder.indexOf(a);
                  const bIndex = categoryOrder.indexOf(b);
                  if (aIndex >= 0 && bIndex >= 0) return aIndex - bIndex;
                  if (aIndex >= 0) return -1;
                  if (bIndex >= 0) return 1;
                  return a.localeCompare(b);
                });
                
                return sortedCategories.map((cat) => (
                  <th key={cat} className="px-3 py-3 text-left cs-label whitespace-nowrap">
                    {cat}
                  </th>
                ));
              })()}
            </tr>
          </thead>
          <tbody style={{ background: 'var(--color-bg-surface)' }}>
            {years.map((year) => {
              const yearKey = `data_${year}`;
              const yearData = editedData[yearKey] || [];
              
              return Array.from({ length: 12 }).map((_, monthIndex) => 
                renderMonthRow(year, monthIndex, yearData[monthIndex])
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-4 cs-meta">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ border: '2px solid var(--color-needs-human-tint-bd)', background: 'var(--color-needs-human-tint)' }}></div>
          <span>Edited cells</span>
        </div>
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
          <span>"Other" = sum of remaining expense categories</span>
        </div>
      </div>
    </div>
  );
}
